import React from 'react';
import {
  ArrowRightIcon,
  Box,
  Button,
  CurrencyIcon,
  CurrencyIconProps,
  FrameworkImage,
  GridContainerWithTitle,
  HorizontalDivider,
  theme,
  TransparentButton,
  Typography,
  VerticalDivider,
  VisibilityBox,
} from '@ttx/design-system';
import {useBreakpoint} from '@ttx/design-system/src/use-breakpoint';

const CurrencyBreakdown: React.FC<{
  traxxContent?: string | null;
  currencySymbol?: CurrencyIconProps['name'] | null;
  usdtContent?: string | null;
  flexValue?: number[];
}> = ({traxxContent, usdtContent, currencySymbol, flexValue}) => {
  return (
    <Box flexDirection="row">
      <Box flex={1}>
        {currencySymbol ? (
          <CurrencyIcon name={currencySymbol} size={18} color="textDefault" />
        ) : null}
        {usdtContent ? (
          <>
            <Box mt="s" />
            <CurrencyIcon name="USDT" size={18} color="greyPrimary" />
          </>
        ) : null}
      </Box>
      <Box flex={flexValue || 4}>
        {traxxContent && (
          <Typography color="textDefault" textStyle="s">
            {traxxContent}
          </Typography>
        )}
        {usdtContent && (
          <>
            <Box mt="s" />

            <Typography color="textLow" textStyle="s">
              {usdtContent}
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
};

const RowItem: React.FC<{
  label: string;
  traxxContent?: string | null;
  usdtContent?: string | null;
  currencySymbol?: CurrencyIconProps['name'] | null;
  withDivider?: boolean;
}> = ({label, traxxContent, usdtContent, currencySymbol, withDivider}) => {
  return (
    <Box flexDirection="row">
      <Box
        w={['auto', 203]}
        paddingY={['three', 's']}
        pl="md"
        flex={[1, 'none']}
      >
        <Typography color="textDefault" textStyle="s">
          {label}
        </Typography>
      </Box>
      {/* {withDivider ? <VerticalDivider /> : null} */}
      <Box
        paddingY={['three', 's']}
        pl="md"
        flex={1}
        justifyContent="space-between"
        borderLeftWidth={withDivider ? 'one' : 'zero'}
        borderLeftColor="outlines"
        borderLeftStyle="solid"
      >
        <CurrencyBreakdown
          traxxContent={traxxContent}
          usdtContent={usdtContent}
          currencySymbol={currencySymbol}
          flexValue={[2, 4]}
        />
      </Box>
    </Box>
  );
};

const QuantitySelector: React.FC<{
  onIncrement: () => void;
  onDecrement: () => void;
  quantity: number;
}> = ({onIncrement, onDecrement, quantity}) => {
  return (
    <Box
      width="100%"
      paddingLeft="md"
      borderTopColor="outlines"
      borderWidth="one"
      borderStyle="solid"
    >
      <Box flexDirection="row">
        <Box flex={1} paddingY={['three', 's']}>
          <Typography textStyle="s" color="textDefault">
            Qty
          </Typography>
        </Box>
        <Box flex={4} flexDirection="row">
          <Box flex={1} paddingY={['three', 's']}>
            <Typography textStyle="s" color="textDefault">
              {quantity}
            </Typography>
          </Box>
          <VerticalDivider />
          <TransparentButton
            onPress={onDecrement}
            style={{justifyContent: 'center'}}
          >
            <Box width={['eleven', 'eight']}>
              <Typography textStyle="s" color="textDefault" textAlign="center">
                -
              </Typography>
            </Box>
          </TransparentButton>
          <VerticalDivider />
          <TransparentButton
            onPress={onIncrement}
            style={{justifyContent: 'center'}}
          >
            <Box width={['eleven', 'eight']} alignItems="center">
              <Typography textStyle="s" color="textDefault" textAlign="center">
                +
              </Typography>
            </Box>
          </TransparentButton>
        </Box>
      </Box>
    </Box>
  );
};

export interface CompleteCheckoutModalProps {
  trackName?: string;
  artist?: string;
  imageSrc?: string;
  price: string | null;
  total: string | null;
  transactionFee: string | null;
  usdtPrice?: string | null;
  usdtTransactionFee?: string | null;
  usdtTotal?: string | null;
  onStartCheckoutPress: () => void;
  onModalClose: () => void;
  currencySymbol?: CurrencyIconProps['name'] | null;
  onIncrement: () => void;
  onDecrement: () => void;
  quantity: number;
  showQuantitySelector?: boolean;
  isMember?: boolean;
}

export const CompleteCheckoutModal: React.FC<CompleteCheckoutModalProps> = ({
  trackName,
  artist,
  imageSrc,
  usdtPrice,
  price,
  transactionFee,
  usdtTransactionFee,
  total,
  usdtTotal,
  onStartCheckoutPress,
  onModalClose,
  currencySymbol,
  onIncrement,
  onDecrement,
  quantity,
  showQuantitySelector,
  isMember = false,
  ...props
}: CompleteCheckoutModalProps) => {
  const {currentBreakpoint} = useBreakpoint();
  const isMobile = currentBreakpoint === '768px';

  return (
    <Box w="100%" maxWidth={[366, 568]} paddingX={['two', 'zero']}>
      <GridContainerWithTitle
        title="Checkout summary"
        onModalClose={onModalClose}
        hasBottomBorder={false}
      >
        <Box flexDirection={['column', 'row']}>
          <FrameworkImage
            src={imageSrc || ''}
            alt={imageSrc || ''}
            width={isMobile ? 366 : 203}
            height={isMobile ? 366 : 215}
          />
          <Box
            flex={1}
            borderLeftWidth={isMobile ? 'zero' : 'one'}
            borderLeftColor="outlines"
            borderLeftStyle="solid"
          >
            <Box m="md" flex={1} justifyContent="space-between">
              <Box>
                <Typography color="textLow" textStyle="s">
                  {artist}
                </Typography>
                <Typography
                  color="textDefault"
                  textStyle="s"
                  css={{marginTop: theme.space.s}}
                >
                  {trackName}
                </Typography>
              </Box>
              {!props?.isJoshSavage && (
                <Box mt={['three', 'zero']}>
                  <CurrencyBreakdown
                    // traxxContent={price}
                    // usdtContent={usdtPrice}
                    traxxContent={isMember ? total : price}
                    usdtContent={isMember ? usdtTotal : usdtPrice}
                    currencySymbol={currencySymbol}
                  />
                </Box>
              )}
            </Box>

            {showQuantitySelector ? (
              <QuantitySelector
                onIncrement={onIncrement}
                onDecrement={onDecrement}
                quantity={quantity}
              />
            ) : null}
          </Box>
        </Box>

        {!props?.isJoshSavage && (
          <>
            <HorizontalDivider />
            <RowItem
              label="Transaction fees"
              // traxxContent={transactionFee}
              traxxContent={isMember ? '0' : transactionFee}
              usdtContent={usdtTransactionFee}
              currencySymbol={currencySymbol}
              withDivider={!isMobile}
            />
            <HorizontalDivider />
            <RowItem
              label="Total"
              traxxContent={total}
              usdtContent={usdtTotal}
              currencySymbol={currencySymbol}
              withDivider={!isMobile}
            />
          </>
        )}
      </GridContainerWithTitle>
      <HorizontalDivider />
      <Box
        flexDirection="row"
        borderWidth="one"
        borderTopWidth="zero"
        borderStyle="solid"
        borderColor="outlines"
        flex={1}
        bg="surfaceDark"
      >
        <Box flex={1}>
          <Button
            type="secondary"
            onPress={onModalClose}
            leftAccessory={<ArrowRightIcon color="outlines" />}
            buttonStyle={{margin: '8px 3px 8px 6px'}}
            hasBorder
          >
            Cancel
          </Button>
        </Box>
        <Box flex={1}>
          <VisibilityBox show={[true, false]}>
            <Button
              type="primary"
              onPress={onStartCheckoutPress}
              buttonStyle={{margin: '8px 6px 8px 3px'}}
              leftAccessory={<ArrowRightIcon color="textInvert" />}
            >
              Checkout
            </Button>
          </VisibilityBox>
          <VisibilityBox show={[false, true]}>
            <Button
              type="primary"
              size="M"
              onPress={onStartCheckoutPress}
              buttonStyle={{margin: '8px 6px 8px 3px'}}
              leftAccessory={<ArrowRightIcon color="textInvert" />}
            >
              Complete checkout
            </Button>
          </VisibilityBox>
        </Box>
      </Box>
    </Box>
  );
};
